import * as React from "react";
import { useState, useEffect, useLayoutEffect } from "react";
import Measure from "react-measure";
import { Map, LatLng, MapProps } from "react-leaflet";
import MapLayers from "./MapLayers";
import MapMarkers from "./MapMarkers";
import { SHOW_DEBUG_INFORMATION } from "./MapComponent";
import { WebViewLeafletEvents, MapLayer, MapMarker, MapShape } from "./models";
import MapShapes from "./MapShapes";
import { LatLngExpression } from "leaflet";

interface MapComponentViewProps extends Omit<MapProps, "children"> {
  addDebugMessage: (msg: any) => void;
  debugMessages: string[];
  mapCenterPosition: LatLng;
  mapLayers: MapLayer[];
  mapMarkers: MapMarker[];
  mapShapes: MapShape[];
  onMapEvent: (mapEvent: WebViewLeafletEvents, payload?: any) => void;
  ownPositionMarker: MapMarker;
  setMapRef: (mapRef: any) => void;
  zoom: number;
  showLayerControl?: boolean;
  debugMap?:boolean
  /**
   * 2026-10-12 : carte agrandie en carré pour pouvoir tourner (guidage dans le sens de la
   * marche). Voir `rotorStyle` plus bas.
   */
  rotorSquare?: boolean;
  /** Appelé après chaque changement de taille ou de forme de la carte. */
  onRotorLayout?: () => void;
}

const MapComponentView = ({
  addDebugMessage,
  debugMessages,
  mapCenterPosition,
  mapLayers = [],
  mapMarkers = [],
  mapShapes = [],
  onMapEvent,
  ownPositionMarker,
  setMapRef,
  zoom = 13,
  // 2026-10-09 : 20 au lieu de 17. La carte est créée avant le premier message de
  // l'application, donc avec la valeur écrite ici, et ses limites de zoom ne changent plus
  // ensuite : le `maxZoom` de 20 que l'application envoie n'a jamais eu d'effet. Avec 17, un
  // zoom au doigt plus proche revenait à 17. 20 est la valeur que l'application demande.
  // (`minZoom` reste à 5 : l'application envoie 10, sans effet non plus, et c'est tant mieux,
  // on peut voir tout le pays.)
  maxZoom = 20,
  minZoom = 5,
  zoomControl = false,
  showLayerControl = false,
  debugMap = false,
  rotorSquare = false,
  onRotorLayout,
  ...rest
}: MapComponentViewProps) => {
  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  const [combinedMapMarkers, setCombinedMapMarkers] = useState([]);

  // 2026-10-12 : la carte est dans un bloc (`#map-rotor`) que le code fait tourner pendant un
  // guidage dans le sens de la marche. Hors de ce cas le bloc a la taille de la zone visible,
  // comme avant. Pendant ce guidage c'est un carré dont le côté vaut la diagonale de la zone
  // visible, centré sur elle : tourné, il la couvre toujours, et ce qui dépasse est coupé par
  // `#map-container`. Le centre du carré est celui de la zone visible : le repère du guidage,
  // posé au centre de la carte, reste au centre de l'écran quel que soit l'angle. Côté pair :
  // le centre de la carte tombe sur un pixel entier.
  const { height, width } = dimensions;
  const side = rotorSquare
    ? 2 * Math.ceil(Math.sqrt(width * width + height * height) / 2)
    : 0;
  const rotorStyle: React.CSSProperties = rotorSquare
    ? {
        position: "absolute",
        left: Math.round((width - side) / 2),
        top: Math.round((height - side) / 2),
        width: side,
        height: side,
        willChange: "transform",
      }
    : { position: "absolute", left: 0, top: 0, width: "100%", height };

  // La carte ne recalcule pas seule sa taille quand ce bloc change (fenêtre redimensionnée
  // après sa création, passage au carré) : le composant le lui demande, avant l'affichage.
  useLayoutEffect(() => {
    if (onRotorLayout) {
      onRotorLayout();
    }
  }, [width, height, rotorSquare, onRotorLayout]);

  useEffect(() => {
    const combinedMapMarkers = mapMarkers;
    if (ownPositionMarker) {
      combinedMapMarkers.push(ownPositionMarker);
    }

    setCombinedMapMarkers(combinedMapMarkers);
  }, [mapMarkers, ownPositionMarker]);

  return (
    <>
      <Measure
        bounds
        onResize={(contentRect) => {
          const { height, width } = contentRect.bounds || {};
          setDimensions({ height, width });
        }}
      >
        {({ measureRef }) => (
          <div
            ref={measureRef}
            id="map-container"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              // backgroundColor: "greenyellow",
              left: 0,
              right: 0,
              overflow: "hidden",
            }}
          >
            {dimensions.height > 0 && (
              <div id="map-rotor" style={rotorStyle}>
              <Map
                ref={(ref) => {
                  setMapRef(ref);
                }}
                center={mapCenterPosition as LatLngExpression}
                onClick={(event: any) => {
                  const { containerPoint, layerPoint, latlng } = event;
                  onMapEvent(WebViewLeafletEvents.ON_MAP_TOUCHED, {
                    containerPoint,
                    layerPoint,
                    touchLatLng: latlng,
                  });
                }}
                onZoomLevelsChange={() => {
                  onMapEvent(WebViewLeafletEvents.ON_ZOOM_LEVELS_CHANGE);
                }}
                onResize={() => {
                  onMapEvent(WebViewLeafletEvents.ON_RESIZE);
                }}
                onZoomStart={() => {
                  onMapEvent(WebViewLeafletEvents.ON_ZOOM_START);
                }}
                onMoveStart={() => {
                  onMapEvent(WebViewLeafletEvents.ON_MOVE_START);
                }}
                onDragstart={() => {
                  onMapEvent(WebViewLeafletEvents.ON_DRAG_START);
                }}
                onZoom={() => {
                  onMapEvent(WebViewLeafletEvents.ON_ZOOM);
                }}
                onMove={() => {
                  onMapEvent(WebViewLeafletEvents.ON_MOVE);
                }}
                onZoomEnd={() => {
                  onMapEvent(WebViewLeafletEvents.ON_ZOOM_END);
                }}
                onMoveEnd={() => {
                  onMapEvent(WebViewLeafletEvents.ON_MOVE_END);
                }}
                onUnload={() => {
                  onMapEvent(WebViewLeafletEvents.ON_UNLOAD);
                }}
                onViewReset={() => {
                  onMapEvent(WebViewLeafletEvents.ON_VIEW_RESET);
                }}
                maxZoom={maxZoom}
                minZoom={minZoom}
                zoom={zoom}
                zoomControl={zoomControl}
                attributionControl={false}
                {...rest}
                style={{ width: "100%", height: "100%" }}
              >
                <MapLayers
                  showLayerControl={showLayerControl}
                  mapLayers={mapLayers}
                />
                <MapMarkers
                  mapMarkers={combinedMapMarkers}
                  onMapEvent={onMapEvent}
                />
                <MapShapes mapShapes={mapShapes} onMapEvent={onMapEvent} />
              </Map>
              </div>
            )}
          </div>
        )}
      </Measure>
      {SHOW_DEBUG_INFORMATION || debugMap ? (
        <div
          style={{
            backgroundColor: "orange",
            maxHeight: "200px",
            overflow: "auto",
            padding: 5,
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 15000,
          }}
          id="messages"
        >
          <ul>
            {debugMessages.map((message, index) => {
              return <li key={index}>{message}</li>;
            })}
          </ul>
        </div>
      ) : null}
    </>
  );
};

export default MapComponentView;
